import React, { useEffect, useRef, useState } from 'react';
import { getLifecycle, toApiError, transitionRecord, validateRecord } from '../../services/pocApi';
import useApiResource from './useApiResource';
import { ConfirmDialog, EmptyState, ErrorNotice, IssueList, LoadingState, StatusBadge } from './PocUi';
import { formatTimestamp } from './pocFormat';

// Validate / activate / retire and the lifecycle audit for one warranty policy
// or authorization rule. Every transition is decided and audited by the backend;
// the buttons only reflect the state it reports.

const NOUN = { policy: 'policy', rule: 'authorization rule' };
const ACTIVATION_TEXT = {
    READY: 'Ready to activate.',
    BLOCKED: 'Activation is blocked until the issues below are resolved.',
    ALREADY_ACTIVE: 'This record is already active.',
    RETIRED: 'Retired records cannot be reactivated.',
    NOT_AVAILABLE: 'Activation is not available on this database (migration 0002 is required).',
};

export function ValidationResult({ result }) {
    if (!result) return null;
    const { activation, issues } = result;
    return (
        <div className="border border-line p-4 space-y-3" role="status" aria-live="polite">
            <p className="text-sm flex flex-wrap items-center gap-3">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">Activation</span>
                <code className={`font-semibold ${activation.status === 'READY' ? 'text-[#14532D]' : activation.status === 'BLOCKED' ? 'text-danger' : ''}`}>
                    {activation.status}
                </code>
                <span>{ACTIVATION_TEXT[activation.status] || activation.message}</span>
            </p>
            {activation.message && ACTIVATION_TEXT[activation.status] && <p className="text-xs text-text-muted">{activation.message}</p>}
            {issues.length
                ? <IssueList issues={issues} />
                : <p className="text-xs text-text-muted">No validation issues were reported.</p>}
        </div>
    );
}

export function LifecycleTimeline({ kind, id, version }) {
    const audit = useApiResource((signal) => getLifecycle(kind, id, signal), [kind, id, version]);
    if (audit.loading && !audit.data) return <LoadingState label="Loading lifecycle audit…" />;
    if (audit.error) return <ErrorNotice error={audit.error} onRetry={audit.reload} title="Lifecycle audit unavailable" />;
    if (!audit.data.length) return <EmptyState>No lifecycle events exist for this {NOUN[kind]} yet.</EmptyState>;
    return (
        <ol className="space-y-3" aria-label="Lifecycle audit">
            {audit.data.map((event) => (
                <li key={event.audit_id} className="border-l-2 border-primary pl-4 text-sm space-y-1">
                    <p className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold uppercase tracking-widest text-xs">{event.action}</span>
                        <StatusBadge status={event.from_status} /> <span aria-hidden="true">→</span> <StatusBadge status={event.to_status} />
                    </p>
                    <p className="text-xs text-text-muted">
                        {formatTimestamp(event.occurred_at)} · Actor: {event.actor || '—'} · Record: {id}
                    </p>
                    <p className="text-[11px] text-text-muted break-all">Fingerprint: <code>{event.record_fingerprint}</code></p>
                </li>
            ))}
        </ol>
    );
}

/**
 * Lifecycle controls for one record. `record` must carry `status`; `label`
 * describes it in confirmations (id and manufacturer).
 */
export function LifecycleActions({ kind, id, status, label, onChanged }) {
    const [validation, setValidation] = useState(null);
    const [validating, setValidating] = useState(false);
    const [error, setError] = useState(null);
    const [dialog, setDialog] = useState(null);           // 'activate' | 'retire'
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const inFlight = useRef(false);

    useEffect(() => {
        setError(null);
        setMessage(null);
    }, [id]);
    // A validation result describes one status; after a transition it must be requested again.
    useEffect(() => setValidation(null), [id, status]);

    const validate = async () => {
        setValidating(true);
        setError(null);
        try {
            setValidation(await validateRecord(kind, id));
        } catch (err) {
            setError(toApiError(err));
        } finally {
            setValidating(false);
        }
    };

    const transition = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setBusy(true);
        setError(null);
        const action = dialog;
        try {
            const { changed, record } = await transitionRecord(kind, id, action);
            setDialog(null);
            setMessage(changed
                ? `${id} is now ${record.status}.`
                : `${id} was already ${record.status}; nothing changed.`);
            onChanged(record);
        } catch (err) {
            setError(toApiError(err));
        } finally {
            inFlight.current = false;
            setBusy(false);
        }
    };

    const canActivate = status === 'DRAFT' && validation?.activation?.status === 'READY';
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
                <button type="button" className="btn btn-secondary" onClick={validate} disabled={validating}>
                    {validating ? 'Validating…' : 'Validate'}
                </button>
                {status === 'DRAFT' && (
                    <button type="button" className="btn btn-primary" onClick={() => { setError(null); setDialog('activate'); }} disabled={!canActivate}
                            title={canActivate ? undefined : 'Validate first; activation is enabled only when the backend reports READY.'}>
                        Activate
                    </button>
                )}
                {status === 'ACTIVE' && (
                    <button type="button" className="btn btn-danger" onClick={() => { setError(null); setDialog('retire'); }}>Retire</button>
                )}
            </div>
            {status === 'DRAFT' && !validation && <p className="text-xs text-text-muted">Validate this draft to enable activation.</p>}
            {message && <p role="status" className="text-sm border-l-2 border-[#1F7A3F] pl-3">{message}</p>}
            {!dialog && <ErrorNotice error={error} />}
            <ValidationResult result={validation} />

            <ConfirmDialog open={dialog === 'activate'} title={`Activate ${id}?`} confirmLabel="Activate" busy={busy}
                           onConfirm={transition} onCancel={() => setDialog(null)}>
                <p><strong>{label}</strong></p>
                <p>The backend re-validates the {NOUN[kind]} and refuses activation if it would conflict with an active record.
                    Once active, it is used for every new evaluation and can no longer be edited.</p>
                <ErrorNotice error={error} title="Activation refused" />
            </ConfirmDialog>
            <ConfirmDialog open={dialog === 'retire'} title={`Retire ${id}?`} confirmLabel="Retire" danger busy={busy}
                           onConfirm={transition} onCancel={() => setDialog(null)}>
                <p><strong>{label}</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Retirement affects future evaluations only.</li>
                    <li>Existing historical decisions remain unchanged.</li>
                    <li>A retired {NOUN[kind]} cannot be reactivated.</li>
                </ul>
                <ErrorNotice error={error} title="Retirement refused" />
            </ConfirmDialog>
        </div>
    );
}
